import React, { useEffect, useState, useCallback } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { 
  FaChartLine, 
  FaChartBar, 
  FaEuroSign, 
  FaShoppingCart, 
  FaArrowUp, 
  FaArrowDown,
  FaEye,
  FaEyeSlash,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaUtensils,
  FaReceipt
} from "react-icons/fa";
import "./AdminHome.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AdminHome = () => {
  const [revenueData, setRevenueData] = useState({
    dailyRevenue: {},
    weeklyRevenue: {},
    monthlyRevenue: {},
  });
  const [showComparison, setShowComparison] = useState({
    daily: false,
    weekly: false,
    monthly: false,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    todayOrders: 0,
    todayRevenue: 0,
    topCategories: []
  });
  const [loading, setLoading] = useState(true);

  const normalizeDate = (date) => {
    if (date.seconds) {
      // Firestore Timestamp object
      return new Date(date.seconds * 1000);
    }
    return new Date(date); // Assume regular Date object
  };

  const calculateLast7DaysRevenue = useCallback((commandes, offset = 0) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    today.setDate(today.getDate() - offset);
  
    const start = new Date(today);
    start.setDate(today.getDate() - 6);
  
    const revenues = Array(7).fill(0);
    const labels = Array(7)
      .fill(0)
      .map((_, i) => {
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        return date.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" });
      });
  
    commandes.forEach((commande) => {
      const orderDate = normalizeDate(commande.date);
      orderDate.setHours(0, 0, 0, 0);
      if (orderDate >= start && orderDate <= today) {
        const diffInDays = Math.round((orderDate - start) / (1000 * 60 * 60 * 24));
        if (diffInDays >= 0 && diffInDays < 7) {
          revenues[diffInDays] += commande.total;
        }
      }
    });
  
    const todayRevenue = revenues[6]; // Dernier jour
    const yesterdayRevenue = revenues[5]; // Avant-dernier jour
    const percentageChange = calculatePercentageChange(todayRevenue, yesterdayRevenue);
  
    return { revenues, labels, percentageChange };
  }, []);  

  const calculateLast4WeeksRevenue = useCallback((commandes, offset = 0) => {
    const today = new Date();
    today.setDate(today.getDate() - offset * 7);
  
    const revenues = Array(4).fill(0);
    const labels = Array(4)
      .fill(0)
      .map((_, i) => {
        const date = new Date(today);
        date.setDate(today.getDate() - i * 7);
        return `Semaine ${getWeekNumber(date)}`;
      })
      .reverse();
  
    commandes.forEach((commande) => {
      const orderDate = normalizeDate(commande.date);
      const weekNumberCommande = getWeekNumber(orderDate);
      const yearCommande = orderDate.getFullYear();
      const diffInWeeks = getWeekNumber(today) - weekNumberCommande;
  
      if (yearCommande === 2024 && weekNumberCommande === 53) {
        revenues[3] += commande.total;
      } else if (diffInWeeks >= 0 && diffInWeeks < 4) {
        revenues[3 - diffInWeeks] += commande.total;
      }
    });
  
    const currentWeekRevenue = revenues[3];
    const previousWeekRevenue = revenues[2];
    const percentageChange = calculatePercentageChange(currentWeekRevenue, previousWeekRevenue);
  
    return { revenues, labels, percentageChange };
  }, []);  

  const calculateLast12MonthsRevenue = useCallback((commandes, offset = 0) => {
    const today = new Date();
    const revenues = Array(12).fill(0);
    const labels = Array(12)
      .fill(0)
      .map((_, i) => {
        const date = new Date(today);
        date.setMonth(today.getMonth() - i - offset);
        return date.toLocaleDateString("fr-FR", { month: "short" });
      })
      .reverse();
  
    commandes.forEach((commande) => {
      const orderDate = normalizeDate(commande.date);
      const diffInMonths = today.getMonth() - orderDate.getMonth() + 12 * (today.getFullYear() - orderDate.getFullYear());
      if (diffInMonths >= offset && diffInMonths < offset + 12) {
        revenues[11 - (diffInMonths - offset)] += commande.total;
      }
    });
  
    const currentMonthRevenue = revenues[11];
    const previousMonthRevenue = revenues[10];
    const percentageChange = calculatePercentageChange(currentMonthRevenue, previousMonthRevenue);
  
    return { revenues, labels, percentageChange };
  }, []);  

  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const dayOfYear = ((date - firstDayOfYear + 86400000) / 86400000);
    const weekNumber = Math.ceil((dayOfYear + firstDayOfYear.getDay()) / 7);
  
    // Gérer les années avec 53 semaines
    const is53WeekYear = (year) => {
      const lastDayOfYear = new Date(year, 11, 31);
      return lastDayOfYear.getDay() === 4 || new Date(year, 11, 30).getDay() === 4;
    };
  
    if (weekNumber === 53 && !is53WeekYear(date.getFullYear())) {
      return 1; // Revenir à la semaine 1 pour les années suivantes
    }
  
    return weekNumber;
  };

  const calculateStats = useCallback((commandes) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const totalOrders = commandes.length;
    const totalRevenue = commandes.reduce((sum, cmd) => sum + (cmd.total || 0), 0);
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    
    const todayOrders = commandes.filter(cmd => {
      const orderDate = normalizeDate(cmd.date);
      orderDate.setHours(0, 0, 0, 0);
      return orderDate.getTime() === today.getTime();
    }).length;
    
    const todayRevenue = commandes.filter(cmd => {
      const orderDate = normalizeDate(cmd.date);
      orderDate.setHours(0, 0, 0, 0);
      return orderDate.getTime() === today.getTime();
    }).reduce((sum, cmd) => sum + (cmd.total || 0), 0);

    // Calculer les catégories les plus populaires
    const categoryCount = {};
    let totalItemsProcessed = 0;
    
    commandes.forEach(cmd => {
      if (cmd.items && Array.isArray(cmd.items)) {
        cmd.items.forEach(item => {
          totalItemsProcessed++;
          const category = item.categorie || 'Autre';
          const quantite = parseInt(item.quantite) || 1;
          categoryCount[category] = (categoryCount[category] || 0) + quantite;
        });
      }
    });
    
    // Debug: afficher la structure des données
    console.log('Total commandes:', totalOrders);
    console.log('Total items traités:', totalItemsProcessed);
    console.log('Catégories trouvées:', categoryCount);
    
    const topCategories = Object.entries(categoryCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([category, count]) => ({ 
        category, 
        count: parseInt(count) || 0
      }));

    // Si aucune catégorie n'est trouvée, retourner des catégories par défaut
    if (topCategories.length === 0) {
      return {
        totalOrders,
        totalRevenue,
        averageOrderValue,
        todayOrders,
        todayRevenue,
        topCategories: [
          { category: 'Aucune donnée', count: 0 }
        ]
      };
    }

    return {
      totalOrders,
      totalRevenue,
      averageOrderValue,
      todayOrders,
      todayRevenue,
      topCategories
    };
  }, []);

  useEffect(() => {
    const fetchRevenueData = async () => {
      setLoading(true);
      try {
        const commandesSnapshot = await getDocs(collection(db, "total_commande"));
        const commandes = commandesSnapshot.docs.map((doc) => doc.data());

        const dailyRevenue = calculateLast7DaysRevenue(commandes);
        const dailyComparison = calculateLast7DaysRevenue(commandes, 7);
        const weeklyRevenue = calculateLast4WeeksRevenue(commandes);
        const weeklyComparison = calculateLast4WeeksRevenue(commandes, 4);
        const monthlyRevenue = calculateLast12MonthsRevenue(commandes);
        const monthlyComparison = calculateLast12MonthsRevenue(commandes, 12);

        const statsData = calculateStats(commandes);

        setRevenueData({
          dailyRevenue: { current: dailyRevenue, comparison: dailyComparison },
          weeklyRevenue: { current: weeklyRevenue, comparison: weeklyComparison },
          monthlyRevenue: { current: monthlyRevenue, comparison: monthlyComparison },
        });

        setStats(statsData);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRevenueData();
  }, [calculateLast7DaysRevenue, calculateLast4WeeksRevenue, calculateLast12MonthsRevenue, calculateStats]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current === 0 ? 0 : 100; // Si le précédent est 0, retournez 100% ou 0%
    return ((current - previous) / previous) * 100;
  };  

  if (loading) {
    return (
      <div className="admin-home-loading-container">
        <div className="admin-home-loading-spinner"></div>
        <p className="admin-home-loading-text">Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="admin-home">
      {/* Header avec titre et date */}
      <div className="admin-home-header">
        <div className="header-content">
          <h1 className="admin-home-title">
            <FaChartLine className="title-icon" />
            Tableau de Bord
          </h1>
          <p className="admin-home-subtitle">
            Vue d'ensemble de votre restaurant - {new Date().toLocaleDateString('fr-FR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>

      {/* Cartes de statistiques principales */}
      <div className="stats-grid">
        <div className="stat-card stat-card-primary">
          <div className="stat-icon">
            <FaEuroSign />
          </div>
          <div className="stat-content">
            <h3>Revenus Totaux</h3>
            <p className="stat-value">{stats.totalRevenue.toFixed(2)}€</p>
            <span className="stat-label">Toutes les commandes</span>
          </div>
        </div>

        <div className="stat-card stat-card-success">
          <div className="stat-icon">
            <FaShoppingCart />
          </div>
          <div className="stat-content">
            <h3>Commandes Totales</h3>
            <p className="stat-value">{stats.totalOrders}</p>
            <span className="stat-label">Nombre total</span>
          </div>
        </div>

        <div className="stat-card stat-card-info">
          <div className="stat-icon">
            <FaReceipt />
          </div>
          <div className="stat-content">
            <h3>Panier Moyen</h3>
            <p className="stat-value">{stats.averageOrderValue.toFixed(2)}€</p>
            <span className="stat-label">Par commande</span>
          </div>
        </div>

        <div className="stat-card stat-card-warning">
          <div className="stat-icon">
            <FaCalendarDay />
          </div>
          <div className="stat-content">
            <h3>Aujourd'hui</h3>
            <p className="stat-value">{stats.todayOrders}</p>
            <span className="stat-label">{stats.todayRevenue.toFixed(2)}€ de revenus</span>
          </div>
        </div>
      </div>

      {/* Graphiques de revenus */}
      <div className="charts-section">
        <h2 className="section-title">
          <FaChartBar className="section-icon" />
          Analyse des Revenus
        </h2>
        
        <div className="charts-grid">
          {["daily", "weekly", "monthly"].map((period) => (
            <div className="chart-card" key={period}>
              <div className="chart-header">
                <div className="chart-title">
                  {period === "daily" && <FaCalendarDay className="chart-icon" />}
                  {period === "weekly" && <FaCalendarWeek className="chart-icon" />}
                  {period === "monthly" && <FaCalendarAlt className="chart-icon" />}
                  <h3>
                    Revenus {period === "daily" ? "Quotidiens" : period === "weekly" ? "Hebdomadaires" : "Mensuels"}
                  </h3>
                </div>
                <div className="chart-controls">
                  {revenueData[`${period}Revenue`]?.current?.percentageChange !== undefined && (
                    <div className={`trend-indicator ${revenueData[`${period}Revenue`].current.percentageChange >= 0 ? 'positive' : 'negative'}`}>
                      {revenueData[`${period}Revenue`].current.percentageChange >= 0 ? 
                        <FaArrowUp /> : <FaArrowDown />
                      }
                      <span>{Math.abs(revenueData[`${period}Revenue`].current.percentageChange).toFixed(1)}%</span>
                    </div>
                  )}
                  {!isMobile && (
                    <button
                      className="comparison-btn"
                      onClick={() => setShowComparison((prev) => ({ ...prev, [period]: !prev[period] }))}
                    >
                      {showComparison[period] ? <FaEyeSlash /> : <FaEye />}
                      {showComparison[period] ? "Masquer" : "Comparer"}
                    </button>
                  )}
                </div>
              </div>
              
              <div className="chart-container">
                {revenueData[`${period}Revenue`]?.current ? (
                  isMobile ? (
                    <Bar
                      data={{
                        labels: [revenueData[`${period}Revenue`].current.labels.slice(-1)[0]],
                        datasets: [{
                          label: `Revenus ${period}`,
                          data: [revenueData[`${period}Revenue`].current.revenues.slice(-1)[0]],
                          backgroundColor: "rgba(37, 99, 235, 0.2)",
                          borderColor: "rgba(37, 99, 235, 1)",
                          borderWidth: 2,
                          borderRadius: 8,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(0, 0, 0, 0.1)'
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <Line
                      data={{
                        labels: revenueData[`${period}Revenue`].current.labels,
                        datasets: [
                          {
                            label: `Revenus ${period === "daily" ? "quotidiens" : period === "weekly" ? "hebdomadaires" : "mensuels"}`,
                            data: revenueData[`${period}Revenue`].current.revenues,
                            borderColor: "rgba(37, 99, 235, 1)",
                            backgroundColor: "rgba(37, 99, 235, 0.1)",
                            fill: true,
                            tension: 0.4,
                            borderWidth: 3,
                            pointBackgroundColor: "rgba(37, 99, 235, 1)",
                            pointBorderColor: "#ffffff",
                            pointBorderWidth: 2,
                            pointRadius: 6,
                          },
                          ...(showComparison[period]
                            ? [
                                {
                                  label: `Revenus (${period === "daily" ? "Semaine dernière" : period === "weekly" ? "4 Semaines précédentes" : "12 Mois précédents"})`,
                                  data: revenueData[`${period}Revenue`].comparison.revenues,
                                  borderColor: "rgba(239, 68, 68, 1)",
                                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                                  fill: true,
                                  tension: 0.4,
                                  borderWidth: 2,
                                  borderDash: [5, 5],
                                  pointBackgroundColor: "rgba(239, 68, 68, 1)",
                                  pointBorderColor: "#ffffff",
                                  pointBorderWidth: 2,
                                  pointRadius: 4,
                                },
                              ]
                            : []),
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top',
                            labels: {
                              usePointStyle: true,
                              padding: 20
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(0, 0, 0, 0.1)'
                            },
                            ticks: {
                              callback: function(value) {
                                return value + '€';
                              }
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            }
                          }
                        },
                        interaction: {
                          intersect: false,
                          mode: 'index'
                        }
                      }}
                    />
                  )
                ) : (
                  <div className="admin-home-chart-loading">
                    <div className="admin-home-chart-spinner"></div>
                    <p className="admin-home-chart-loading-text">Chargement des données...</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section des catégories populaires */}
      {stats.topCategories.length > 0 && (
        <div className="categories-section">
          <h2 className="section-title">
            <FaUtensils className="section-icon" />
            Catégories Populaires
          </h2>
          <div className="categories-grid">
            {stats.topCategories.map((category, index) => (
              <div key={category.category} className="category-card">
                <div className="category-rank">#{index + 1}</div>
                <div className="category-info">
                  <h4>{category.category}</h4>
                  <p>{category.count} commandes</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHome;
